import Pusher from 'pusher-js';
import {
  isPusherEnabled,
  NEXT_PUBLIC_PUSHER_CLUSTER,
  NEXT_PUBLIC_PUSHER_KEY
} from '@utils/constants';

export interface PusherChannelLike {
  bind(_eventName: string, _callback: (_data: unknown) => void): unknown;
  unbind(_eventName?: string, _callback?: (_data: unknown) => void): unknown;
  unsubscribe(): void;
}

export interface PusherClientLike {
  disconnect: () => void;
  subscribe: (_channelName: string) => PusherChannelLike;
  unsubscribe: (_channelName: string) => void;
}

// Stand-ins used when Pusher is off, so consumers can bind, unbind and unsubscribe as usual.
// subscribe returns a new channel each time, since some consumers reset state when the channel changes.
const createNoopPusherChannel = (): PusherChannelLike => ({
  bind: (_eventName, _callback) => {},
  unbind: (_eventName, _callback) => {},
  unsubscribe: () => {},
});

const noopPusher: PusherClientLike = {
  disconnect: () => {},
  subscribe: _channelName => createNoopPusherChannel(),
  unsubscribe: _channelName => {},
};

let pusherClient: Pusher | null = null;

/**
 * Returns the tab's single Pusher client, creating it on first use. pusher-js never releases a
 * client on its own (Pusher.instances keeps every one), so it is shared across provider remounts
 * and only closed by disconnectPusherClient on sign-out.
 * @returns {PusherClientLike} The shared client, or a do-nothing stand-in when Pusher is off
 */
export const getPusherClient = (): PusherClientLike => {
  if (!isPusherEnabled) return noopPusher;

  pusherClient ??= new Pusher(NEXT_PUBLIC_PUSHER_KEY ?? '', { cluster: NEXT_PUBLIC_PUSHER_CLUSTER });

  return pusherClient;
};

export const disconnectPusherClient = () => {
  pusherClient?.disconnect();
  pusherClient = null;
};
